import { useEffect, useMemo, useState } from "react";
import useApiSource from "../../components/terminal/useApiSource.js";
import { terminalAPI } from "../../apis/TerminalAPI.jsx";
import { portfolioAPI } from "../../apis/PortfolioAPI.jsx";
import { swapAPI } from "../../apis/SwapAPI.jsx";
import { flattenTxns, prettyTxnSubtype } from "../home/TreasurySummary.jsx";
import {
  MOCK_POSITIONS,
  MOCK_TL_EVENTS,
  MOCK_INFLIGHT,
  MOCK_PARTIES,
  MOCK_FX_WINDOWS,
  MOCK_ACTIVITY,
  MOCK_COUNTRIES,
  MOCK_COUNTRY_VOLUME,
  MOCK_CORRIDORS,
} from "./mockData.js";

const CURRENCY_LABEL = {
  USD:"United States Dollar", EUR:"Euro", GBP:"Pound Sterling",
  JPY:"Japanese Yen", SGD:"Singapore Dollar", AED:"UAE Dirham",
  USDC:"USD Coin · multi-chain", USDT:"Tether",
  BTC:"Bitcoin", ETH:"Ether", SOL:"Solana", stETH:"Staked Ether",
};

/* Adapter hooks for the Overview tab. One hook per spec section so
   each pane fails independently — Globe can render live while
   Counterparties is degraded, and the user sees per-pane status.

   Refresh intervals are tuned per the data's volatility:
     - global flow / corridors / counterparties: 30s
     - inflight + timeline: 10s (more active)
     - FX windows: 5s (intraday rates)
   The activity ticker has its own WebSocket hook (see useActivityStream.js
   for §A.7). */

const ZERO_OR_MOCK = (live, mock) => (Array.isArray(live) && live.length ? live : mock);

/* Globe data lives in two endpoints (countries + corridors). We
   adapt §A.1 into the {iso, lat, lon, label} + countryVolume map
   that GlobePane expects. Lat/lon comes server-side; if missing we
   fall through to MOCK_COUNTRIES coordinates as a positional crutch. */
export function useGlobeData(token) {
  const flow      = useApiSource({ token, fetcher: terminalAPI.getGlobalFlow24h.bind(terminalAPI),  fallback: null, intervalMs: 30_000 });
  const corridors = useApiSource({ token, fetcher: terminalAPI.getCorridorsActive.bind(terminalAPI), fallback: null, intervalMs: 30_000 });

  const { countries, countryVolume } = useMemo(() => {
    const list = flow.data?.countries || [];
    if (list.length === 0) {
      return { countries: MOCK_COUNTRIES, countryVolume: MOCK_COUNTRY_VOLUME };
    }
    const cv = {};
    const cs = [];
    list.forEach((c) => {
      cv[c.iso] = { in: c.in_usd, out: c.out_usd, gross: c.gross_usd };
      const seed = MOCK_COUNTRIES.find((m) => m.iso === c.iso);
      cs.push({
        iso:   c.iso,
        label: c.label || seed?.label || c.iso,
        lat:   c.lat ?? seed?.lat ?? 0,
        lon:   c.lon ?? seed?.lon ?? 0,
      });
    });
    return { countries: cs, countryVolume: cv };
  }, [flow.data]);

  const corridorsOut = useMemo(() => {
    const list = corridors.data?.corridors;
    return Array.isArray(list) && list.length ? list : MOCK_CORRIDORS;
  }, [corridors.data]);

  /* The pane's worst status drives the badge — if either feed is
     degraded the user sees that. */
  const status = (flow.status === "live" && corridors.status === "live")
    ? "live"
    : (flow.status === "feature_off" || corridors.status === "feature_off")
      ? "feature_off"
      : (flow.status === "loading" && corridors.status === "loading")
        ? "loading"
        : "degraded";

  return { countries, countryVolume, corridors: corridorsOut, status };
}

/* Helpers shared by the derived hooks. Each derivation reads from the
   live txn-history endpoint (the same one usePositions / useActivity
   already use), so we don't introduce a new dependency surface — just
   a different projection of data we already fetch.

   Critical invariant: every derivation falls back to MOCK_* when both
   the spec endpoint and the derived computation produce nothing. The
   prior rollback was caused by short-circuiting to [] in that case,
   leaving panes visibly empty when the user is just early in their
   journey. The pane must always render. */

const IN_DIRECTIONS = new Set([
  "DEPOSIT", "BUY", "CONVERSION_DEPOSIT",
  "WIRE_IN_DOMESTIC", "WIRE_IN_INTERNATIONAL",
]);
function isInbound(t) {
  if (IN_DIRECTIONS.has(t.transaction_type)) return true;
  if (t.transaction_subtype === "RECEIVED_FROM_PEER") return true;
  return false;
}
function railOf(t) {
  return String(t.rail || t.transaction_type || "WIRE").toUpperCase().replace("WITHDRAW", "WIRE");
}
function counterpartyOf(t) {
  return t.counterparty_name || t.peer_name || t.vendor_name || t.beneficiary_name || "";
}

/* Settlement timeline §A.3 — derives from recent transactions when
   the spec endpoint isn't available. Each txn within -6h..+72h
   becomes an event at its hour offset; everything outside that
   window is filtered out so the strip only shows what fits. */
export function useTimelineEvents(token) {
  const r = useApiSource({
    token,
    fetcher: terminalAPI.getSettlementTimeline.bind(terminalAPI),
    fallback: null,
    intervalMs: 10_000,
  });
  const [derived, setDerived] = useState(null);

  useEffect(() => {
    if (!token) { setDerived(null); return; }
    if (Array.isArray(r.data?.events) && r.data.events.length) return; // spec endpoint is serving
    let cancelled = false;
    portfolioAPI.getTransactionHistoryPaginated(token, 0, 50)
      .then((resp) => {
        if (cancelled) return;
        const flat = flattenTxns(resp?.data);
        const now = Date.now();
        const mapped = flat.map((t) => {
          const ts = new Date(t.creation_time || t.created_at || 0).getTime();
          if (!Number.isFinite(ts)) return null;
          const hours = (ts - now) / 3_600_000;
          if (hours < -6 || hours > 72) return null;
          return {
            id:             t.transaction_id || t.id,
            t_offset_hours: hours,
            direction:      isInbound(t) ? "in" : "out",
            rail:           railOf(t),
            amount:         t.qty || t.total_main_qty || "0",
            currency:       t.base_currency || "USD",
            counterparty:   counterpartyOf(t) || "—",
            reference:      (t.transaction_id || "").slice(-6),
            source:         "settled",
          };
        }).filter(Boolean);
        setDerived(mapped);
      })
      .catch(() => { if (!cancelled) setDerived([]); });
    return () => { cancelled = true; };
  }, [token, r.data]);

  const events = Array.isArray(r.data?.events) && r.data.events.length
    ? r.data.events
    : (Array.isArray(derived) && derived.length ? derived : MOCK_TL_EVENTS);
  const status = (Array.isArray(r.data?.events) && r.data.events.length) ? r.status
              : (Array.isArray(derived) && derived.length) ? "live"
              : r.status;
  return { events, status };
}

/* Inflight §A.4 — derives from txns whose status indicates they
   haven't settled (HOLD / pending / processing / in_flight). Caps at
   25 entries so the rail stays readable. */
export function useInflight(token) {
  const r = useApiSource({
    token,
    fetcher: terminalAPI.getInflight.bind(terminalAPI),
    fallback: null,
    intervalMs: 10_000,
  });
  const [derived, setDerived] = useState(null);

  useEffect(() => {
    if (!token) { setDerived(null); return; }
    if (Array.isArray(r.data?.items) && r.data.items.length) return;
    let cancelled = false;
    portfolioAPI.getTransactionHistoryPaginated(token, 0, 100)
      .then((resp) => {
        if (cancelled) return;
        const flat = flattenTxns(resp?.data);
        const pending = flat.filter((t) => {
          const tt = String(t.transaction_type || "").toUpperCase();
          const st = String(t.status || "").toLowerCase();
          return tt === "HOLD" || st.includes("pend") || st.includes("processing") || st.includes("in_flight");
        });
        setDerived(pending.slice(0, 25).map((t) => ({
          id:           t.transaction_id || t.id,
          counterparty: counterpartyOf(t) || "—",
          rail:         railOf(t),
          amount:       t.qty || t.total_main_qty || "0",
          currency:     t.base_currency || "USD",
          created_at:   t.creation_time || t.created_at || "",
          eta_label:    "—",
        })));
      })
      .catch(() => { if (!cancelled) setDerived([]); });
    return () => { cancelled = true; };
  }, [token, r.data]);

  const items = Array.isArray(r.data?.items) && r.data.items.length
    ? r.data.items
    : (Array.isArray(derived) && derived.length ? derived : MOCK_INFLIGHT);
  const status = (Array.isArray(r.data?.items) && r.data.items.length) ? r.status
              : (Array.isArray(derived) && derived.length) ? "live"
              : r.status;
  return { items, status };
}

/* Top counterparties §A.5 — aggregates 30d txn history by name +
   rail, returns top 8 by absolute volume. Net flow is signed so the
   pane can render direction at a glance. */
export function useCounterparties(token) {
  const r = useApiSource({
    token,
    fetcher: terminalAPI.getCounterpartiesTop.bind(terminalAPI),
    fallback: null,
    intervalMs: 60_000,
  });
  const [derived, setDerived] = useState(null);

  useEffect(() => {
    if (!token) { setDerived(null); return; }
    if (Array.isArray(r.data?.items) && r.data.items.length) return;
    let cancelled = false;
    portfolioAPI.getTransactionHistoryPaginated(token, 0, 200)
      .then((resp) => {
        if (cancelled) return;
        const flat = flattenTxns(resp?.data);
        const cutoff = Date.now() - 30 * 24 * 60 * 60 * 1000;
        const byName = {};
        flat.forEach((t) => {
          const name = counterpartyOf(t);
          if (!name) return;
          const ts = new Date(t.creation_time || t.created_at || 0).getTime();
          if (Number.isFinite(ts) && ts < cutoff) return;
          const qty = Math.abs(Number(t.qty || t.total_main_qty || 0));
          const key = name.toLowerCase();
          if (!byName[key]) {
            byName[key] = {
              name,
              iso:            "",
              channel_id:     (t.counterparty_id || "").slice(-6) || "—",
              channel_label:  railOf(t),
              volume_30d_usd: 0,
              net_30d_usd:    0,
            };
          }
          byName[key].volume_30d_usd += qty;
          byName[key].net_30d_usd   += isInbound(t) ? qty : -qty;
        });
        const top = Object.values(byName)
          .sort((a, b) => b.volume_30d_usd - a.volume_30d_usd)
          .slice(0, 8);
        setDerived(top);
      })
      .catch(() => { if (!cancelled) setDerived([]); });
    return () => { cancelled = true; };
  }, [token, r.data]);

  const parties = Array.isArray(r.data?.items) && r.data.items.length
    ? r.data.items
    : (Array.isArray(derived) && derived.length ? derived : MOCK_PARTIES);
  const status = (Array.isArray(r.data?.items) && r.data.items.length) ? r.status
              : (Array.isArray(derived) && derived.length) ? "live"
              : r.status;
  return { parties, status };
}

/* FX windows §A.6 — surfaces still-valid locked rates from the
   legacy swap surface. Each lock maps to a {pair, rate, expiry_iso}
   row so PartiesPane's strip renders identically to the spec shape. */
export function useFxWindows(token) {
  const r = useApiSource({
    token,
    fetcher: terminalAPI.getFxWindows.bind(terminalAPI),
    fallback: null,
    intervalMs: 5_000,
  });
  const [derived, setDerived] = useState(null);

  useEffect(() => {
    if (!token) { setDerived(null); return; }
    if (Array.isArray(r.data?.pairs) && r.data.pairs.length) return;
    let cancelled = false;
    swapAPI.getLockedRates(token)
      .then((resp) => {
        if (cancelled) return;
        const list = Array.isArray(resp?.data) ? resp.data : [];
        const now = Date.now();
        const mapped = list
          .filter((lr) => {
            const exp = new Date(lr.expires_on || lr.expires_at || 0).getTime();
            return Number.isFinite(exp) && exp > now;
          })
          .map((lr) => ({
            pair:        `${lr.from_currency || ""}/${lr.to_currency || ""}`,
            rate:        String(lr.rate || lr.locked_rate || ""),
            expiry_iso:  lr.expires_on || lr.expires_at || "",
            id:          lr.id || lr.locked_rate_id || "",
          }));
        setDerived(mapped);
      })
      .catch(() => { if (!cancelled) setDerived([]); });
    return () => { cancelled = true; };
  }, [token, r.data]);

  const windows = Array.isArray(r.data?.pairs) && r.data.pairs.length
    ? r.data.pairs
    : (Array.isArray(derived) && derived.length ? derived : MOCK_FX_WINDOWS);
  const status = (Array.isArray(r.data?.pairs) && r.data.pairs.length) ? r.status
              : (Array.isArray(derived) && derived.length) ? "live"
              : r.status;
  return { windows, status };
}

/* Positions — wired to /portfolio/get_default +
   /portfolio/get_portfolio_balances. Non-USD balances are converted
   to USD-equivalent via swapAPI.convertEstimate (same pattern as the
   legacy TreasurySummary) so the pane's USD column matches the
   design's intent. Math.sign preserves negative balances since
   convertEstimate is sign-agnostic. */
export function usePositions(token) {
  const [positions, setPositions] = useState(MOCK_POSITIONS);
  const [status, setStatus]       = useState(token ? "loading" : "fallback");

  useEffect(() => {
    if (!token) { setStatus("fallback"); setPositions(MOCK_POSITIONS); return; }
    let cancelled = false;
    let intervalId = null;

    const fetchOnce = async () => {
      try {
        const def = await portfolioAPI.getDefaultPortfolio(token);
        if (!def?.data?.name) throw new Error("no_default_portfolio");
        const bal = await portfolioAPI.getPortfolioBalances(token, def.data.name);
        if (cancelled) return;
        const rows = Array.isArray(bal?.data) ? bal.data : [];
        const nonZero = rows.filter((b) => Number(b.qty) !== 0);
        if (nonZero.length === 0) {
          setStatus("live");           // backend OK, just empty
          setPositions([]);
          return;
        }
        const usdItems = nonZero.filter((b) => b.base_currency === "USD");
        const nonUsd   = nonZero.filter((b) => b.base_currency !== "USD");
        const conversions = await Promise.all(
          nonUsd.map((b) =>
            swapAPI
              .convertEstimate(token, Math.abs(Number(b.qty)), b.base_currency, "USD", 0)
              .catch(() => null)
          )
        );
        if (cancelled) return;
        const mapped = [];
        usdItems.forEach((b) => {
          const qty = Number(b.qty) || 0;
          mapped.push({ sym: "USD", label: CURRENCY_LABEL.USD, qty, usd: qty, chgPct: 0 });
        });
        nonUsd.forEach((b, i) => {
          const qty  = Number(b.qty) || 0;
          const conv = conversions[i]?.data?.amount_to_receive;
          const usd  = conv != null ? Number(conv) * Math.sign(qty) : qty;
          mapped.push({
            sym:    b.base_currency,
            label:  CURRENCY_LABEL[b.base_currency] || b.base_currency,
            qty, usd, chgPct: 0,
          });
        });
        mapped.sort((a, b) => Math.abs(b.usd) - Math.abs(a.usd));
        setPositions(mapped);
        setStatus("live");
      } catch (e) {
        if (cancelled) return;
        const code = e?.response?.status;
        setStatus(code === 404 ? "feature_off" : "degraded");
        setPositions(MOCK_POSITIONS);
      }
    };

    fetchOnce();
    intervalId = setInterval(fetchOnce, 30_000);
    return () => { cancelled = true; if (intervalId) clearInterval(intervalId); };
  }, [token]);

  return { positions, status };
}

/* railFromTxn — map the legacy transaction_type / transaction_subtype
   enum into one of the RailTag keys (SWIFT, Wire, SEPA, ACH, FPS,
   USDC, USDT, BTC, ETH, FX, CARD). Anything unmapped falls through
   to "Wire" so the ticker never renders the raw SCREAMING_SNAKE_CASE
   that the BE delivers. */
function railFromTxn(t) {
  const type    = String(t.transaction_type || "").toUpperCase();
  const subtype = String(t.transaction_subtype || "").toUpperCase();
  const cur     = String(t.base_currency || "").toUpperCase();
  if (subtype === "BLOXCARD_ADD_FUNDS" || subtype === "BLOXCARD_REMOVE_FUNDS") return "CARD";
  if (subtype === "SENT_TO_PEER" || subtype === "RECEIVED_FROM_PEER") {
    return ["USDC","USDT","BTC","ETH"].includes(cur) ? cur : "Wire";
  }
  if (/WIRE_(IN|OUT)_INTERNATIONAL/.test(type)) return "SWIFT";
  if (/WIRE_(IN|OUT)_DOMESTIC/.test(type) || type === "WITHDRAWAL") return "Wire";
  if (type === "BUY" || type === "SELL" || /CONVERSION/.test(type)) return "FX";
  if (type === "DEPOSIT") {
    if (cur === "USD") return "ACH";
    if (cur === "EUR") return "SEPA";
    if (cur === "GBP") return "FPS";
    if (["USDC","USDT","BTC","ETH"].includes(cur)) return cur;
    return "Wire";
  }
  if (["USDC","USDT","BTC","ETH"].includes(cur)) return cur;
  return "Wire";
}

/* Activity — when no WebSocket URL is configured (the §A.7 stream
   is the canonical surface but it may not be deployed yet), this
   hook polls the existing /portfolio/get_deep_portfolio_info_pageable
   endpoint and reshapes via the same flattenTxns adapter the legacy
   Recent Activity panel uses. Returns events shaped for the
   ActivityTicker (ts, rail, counterparty, reference, amount, currency,
   status). */
export function useActivityPolled(token) {
  const [events, setEvents] = useState(MOCK_ACTIVITY);
  const [status, setStatus] = useState(token ? "loading" : "fallback");

  useEffect(() => {
    if (!token) { setStatus("fallback"); setEvents(MOCK_ACTIVITY); return; }
    let cancelled = false;

    const tick = async () => {
      try {
        const r = await portfolioAPI.getTransactionHistoryPaginated(token, 0, 50);
        if (cancelled) return;
        const flat = flattenTxns(r?.data);
        if (!flat.length) { setStatus("live"); setEvents([]); return; }
        const mapped = flat.slice(0, 100).map((t) => {
          const qty = Number(t.qty) || 0;
          const isDebit = t.transaction_type === "WITHDRAWAL" || t.transaction_type === "SEND"
                       || t.transaction_subtype === "SENT_TO_PEER";
          const signed = isDebit ? -Math.abs(qty) : Math.abs(qty);
          const tsRaw = t.creation_time || t.created_at || "";
          const ts = String(tsRaw).slice(11, 19) || "";
          const status = t.status?.toLowerCase().includes("complete") ? "settled"
                       : t.status?.toLowerCase().includes("pend")    ? "in_flight"
                       : t.status?.toLowerCase().includes("fail")    ? "failed"
                       : "queued";
          const desc = String(t.description || "").trim();
          const counterparty =
            t.beneficiary_name
            || t.from_display
            || (desc && desc.length <= 32 ? desc : "")
            || prettyTxnSubtype(t)
            || "—";
          return {
            id:          t.transaction_id || t.id,
            ts,
            rail:        railFromTxn(t),
            counterparty,
            reference:   t.transaction_id?.slice(-10) || t.id?.slice(-10) || "",
            amount:      String(signed),
            currency:    t.base_currency || "USD",
            status,
          };
        });
        setEvents(mapped);
        setStatus("live");
      } catch (e) {
        if (cancelled) return;
        const code = e?.response?.status;
        setStatus(code === 404 ? "feature_off" : "degraded");
        setEvents(MOCK_ACTIVITY);
      }
    };

    tick();
    const intervalId = setInterval(tick, 15_000);
    return () => { cancelled = true; clearInterval(intervalId); };
  }, [token]);

  return { events, status };
}
